/**
 * Edit a saved response as rich text.
 *
 * The model gets things wrong, and until now the only repair was to redo the
 * whole turn. This opens the stored markdown in a WYSIWYG editor — tables stay
 * tables while you edit them — and saves it back through the same endpoint the
 * dictation editor uses, which deletes the facts drawn from the old text and
 * re-extracts from the new. Values that live in an agent's tables are the
 * database manager's business; this is for the prose around them.
 */

import { getHeaders } from './api.js';
import { assistantMarkdownToHtml } from './ui.js';
import { state } from './state.js';

let editor = null;
let overlay = null;
let current = null; // { messageEl, messageId, thinking }

/** Assistant text is stored as <think>…</think> followed by the reply. */
function splitThinking(raw) {
    const text = raw || '';
    const start = text.indexOf('<think>');
    const end = text.indexOf('</think>');
    if (start === -1 || end === -1 || start > end) {
        return { thinking: null, body: text };
    }
    return {
        thinking: text.slice(start, end + 8),
        body: text.slice(end + 8).replace(/^\n+/, ''),
    };
}

/** Put the reasoning trace back, so editing prose does not destroy it. */
function rejoinThinking(thinking, body) {
    return thinking ? `${thinking}\n${body}` : body;
}

function buildOverlay() {
    const el = document.createElement('div');
    el.className = 'msg-editor-overlay';
    el.innerHTML = `
        <div class="msg-editor-panel">
            <div class="msg-editor-header">
                <span class="msg-editor-title">Edit response</span>
                <div class="msg-editor-window-btns">
                    <button class="msg-editor-btn" data-act="maximize" title="Maximize" aria-label="Maximize">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M16 21h3a2 2 0 0 0 2-2v-3M8 21H5a2 2 0 0 1-2-2v-3"/>
                        </svg>
                    </button>
                    <button class="msg-editor-btn" data-act="minimize" title="Restore to normal size" aria-label="Restore to normal size" disabled>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M4 10h4V6M20 10h-4V6M4 14h4v4M20 14h-4v4"/>
                        </svg>
                    </button>
                    <button class="msg-editor-btn" data-act="close" title="Close" aria-label="Close">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div class="msg-editor-body"><div class="msg-editor-mount"></div></div>
            <div class="msg-editor-footer">
                <span class="msg-editor-note"></span>
                <div class="msg-editor-actions">
                    <button class="msg-editor-cancel">Cancel</button>
                    <button class="msg-editor-save">Save</button>
                </div>
            </div>
        </div>
    `;
    document.body.appendChild(el);

    const panel = el.querySelector('.msg-editor-panel');
    const maxBtn = el.querySelector('[data-act="maximize"]');
    const minBtn = el.querySelector('[data-act="minimize"]');
    // Each button is lit only when it has somewhere to go, as on the KB panel.
    const setMaximized = (on) => {
        panel.classList.toggle('maximized', on);
        maxBtn.disabled = on;
        minBtn.disabled = !on;
    };
    maxBtn.addEventListener('click', () => setMaximized(true));
    minBtn.addEventListener('click', () => setMaximized(false));
    el.querySelector('[data-act="close"]').addEventListener('click', close);
    el.querySelector('.msg-editor-cancel').addEventListener('click', close);
    el.querySelector('.msg-editor-save').addEventListener('click', save);
    el.addEventListener('mousedown', (e) => {
        if (e.target === el) close();
    });
    return el;
}

function setNote(text, kind = '') {
    const note = overlay?.querySelector('.msg-editor-note');
    if (note) {
        note.textContent = text || '';
        note.className = `msg-editor-note${kind ? ' ' + kind : ''}`;
    }
}

export function close() {
    if (editor) {
        try {
            editor.destroy();
        } catch {
            /* the editor is going away regardless */
        }
        editor = null;
    }
    overlay?.remove();
    overlay = null;
    current = null;
    document.removeEventListener('keydown', onKeydown);
}

function onKeydown(e) {
    if (e.key === 'Escape') close();
}

/**
 * Open the editor for one assistant message.
 *
 * Reads the stored text from the server rather than the rendered bubble, so
 * what you edit is what is saved.
 */
export async function openMessageEditor(messageEl, messageId) {
    if (!messageId) return;
    close();
    overlay = buildOverlay();
    document.addEventListener('keydown', onKeydown);
    setNote('Loading…');

    let raw = '';
    try {
        const res = await fetch(
            `/sessions/${state.sessionId}/messages/${messageId}/content`,
            { headers: getHeaders() }
        );
        if (!res.ok) throw new Error(`load failed (${res.status})`);
        raw = (await res.json()).content || '';
    } catch (err) {
        setNote(`Could not load this message: ${err.message}`, 'error');
        return;
    }

    const { thinking, body } = splitThinking(raw);
    current = { messageEl, messageId, thinking };

    const Editor = window.toastui?.Editor;
    if (!Editor) {
        setNote('The editor failed to load. Try a hard refresh.', 'error');
        return;
    }
    const dark = !document.body.classList.contains('light-theme');
    editor = new Editor({
        el: overlay.querySelector('.msg-editor-mount'),
        height: '100%',
        initialEditType: 'wysiwyg',
        previewStyle: 'vertical',
        initialValue: body,
        usageStatistics: false,
        theme: dark ? 'dark' : 'default',
        toolbarItems: [
            ['heading', 'bold', 'italic', 'strike'],
            ['hr', 'quote'],
            ['ul', 'ol', 'task'],
            ['table', 'link'],
            ['code', 'codeblock'],
        ],
    });
    steadyEnterOnTouch(editor);
    setNote(thinking ? 'The reasoning trace is preserved and not shown here.' : '');
}

/**
 * Make Enter split the line at the caret on a phone.
 *
 * A phone's keyboard splits the line in the page first and the editor catches
 * up afterwards, by running its own Enter rules over what it had before. One
 * of the library's rules (for a front-matter block, which a response never
 * has) measures the page before it checks whether it applies at all, and the
 * page no longer matches: the measurement threw, the split was dropped, and a
 * moment later the fallback pressed Enter again with the caret back at the
 * start of the line. So a blank line appeared above the whole line instead of
 * a break where the caret was. A measurement that cannot be taken now answers
 * "not at the edge", which is what lets the ordinary split run.
 */
function steadyEnterOnTouch(ed) {
    for (const view of [ed?.wwEditor?.view, ed?.mdEditor?.view]) {
        if (!view || typeof view.endOfTextblock !== 'function') continue;
        const measure = view.endOfTextblock.bind(view);
        view.endOfTextblock = (dir, at) => {
            try {
                return measure(dir, at);
            } catch {
                return false;
            }
        };
    }
}

async function save() {
    if (!editor || !current) return;
    const saveBtn = overlay.querySelector('.msg-editor-save');
    const body = editor.getMarkdown();
    if (!body.trim()) {
        setNote('Nothing to save — the response would be empty.', 'error');
        return;
    }
    saveBtn.disabled = true;
    setNote('Saving…');
    try {
        const res = await fetch(
            `/sessions/${state.sessionId}/messages/${current.messageId}/content`,
            {
                method: 'PUT',
                headers: getHeaders(true),
                body: JSON.stringify({
                    content: rejoinThinking(current.thinking, body),
                    // An edit is a redo of the facts: the old ones are dropped
                    // and drawn again from the text that now stands.
                    reextract_facts: true,
                    model: state.loadedModel || null,
                }),
            }
        );
        if (!res.ok) throw new Error(`save failed (${res.status})`);
        const data = await res.json().catch(() => ({}));
        applyToPage(current.messageEl, body, !!data.extracting);
        close();
    } catch (err) {
        saveBtn.disabled = false;
        setNote(`Could not save: ${err.message}`, 'error');
    }
}

/** Redraw the bubble and show the extraction spinner the footer already uses. */
function applyToPage(messageEl, body, extracting) {
    const contentEl = messageEl?.querySelector('.message-content');
    if (contentEl && window.marked) {
        // The same renderer the stream uses, so a saved edit looks exactly
        // like the response it replaced — blank lines and all.
        contentEl.innerHTML = assistantMarkdownToHtml(body);
    }
    messageEl?.classList.add('was-edited');
    if (!extracting) return;
    const bar = messageEl?.querySelector('.message-actions');
    if (bar && !bar.querySelector('.extraction-status')) {
        const spinner = document.createElement('div');
        spinner.className = 'extraction-status';
        spinner.innerHTML = '<div class="mini-spinner"></div><span>Re-extracting facts...</span>';
        bar.insertBefore(spinner, bar.firstChild);
        pollExtraction(spinner);
    }
}

function pollExtraction(spinner) {
    let tries = 0;
    const tick = async () => {
        tries += 1;
        try {
            const res = await fetch(`/sessions/${state.sessionId}/extraction_status`, {
                headers: getHeaders(),
            });
            const data = await res.json();
            if (!data.extracting) {
                spinner.remove();
                return;
            }
        } catch {
            /* a failed poll is not worth reporting; the spinner times out */
        }
        if (tries > 60) {
            spinner.remove();
            return;
        }
        setTimeout(tick, 1000);
    };
    setTimeout(tick, 1000);
}

window.openMessageEditor = openMessageEditor;
